import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./BecomePartner.css";
import { ArrowLeft } from "lucide-react";
import { Country, State, City } from "country-state-city";

export default function BecomePartner() {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    phone_number: "",
    city: "",
    state: "",
    country: "",
  });

  // Dropdown data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // Feedback state
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");

  // on mount → load countries
  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  // when country changes → load states
  useEffect(() => {
    if (formData.country) {
      const sts = State.getStatesOfCountry(formData.country);
      setStates(sts);
      setCities([]);
      setFormData((f) => ({ ...f, state: "", city: "" }));
    }
  }, [formData.country]);

  // when state changes → load cities
  useEffect(() => {
    if (formData.country && formData.state) {
      const cts = City.getCitiesOfState(formData.country, formData.state);
      setCities(cts);
      setFormData((f) => ({ ...f, city: "" }));
    }
  }, [formData.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone_number) {
      setError("Name and Phone Number are required");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication required. Please login.");

      const res = await fetch(
        "https://api.rtoxpert.com/api/partners/become-partner",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Submission failed");

      // **NEW** show popup and navigate after 2s
      setSuccess("Admin will reach out to you");
      setFormData({
        name: "",
        phone_number: "",
        city: "",
        state: "",
        country: "",
      });

      setTimeout(() => {
        navigate("/profile");
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="become-partner-container">
      <div className="header">
        <button
          type="button"
          className="back-button"
          onClick={() => navigate("/profile")}
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <h2>Become a Partner</h2>
      </div>

      {error   && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <form onSubmit={handleSubmit} className="form-container">
        <div className="form-group">
          <input
            type="text"
            name="name"
            placeholder="Name *"
            className="form-input"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <input
            type="text"
            name="phone_number"
            placeholder="Phone Number *"
            className="form-input"
            value={formData.phone_number}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <div className="select-wrapper">
            <select
              name="country"
              className="form-select"
              value={formData.country}
              onChange={handleChange}
              required
            >
              <option value="" disabled>
                Select Country
              </option>
              {countries.map((c) => (
                <option key={c.isoCode} value={c.isoCode}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group half">
            <div className="select-wrapper">
              <select
                name="state"
                className="form-select"
                value={formData.state}
                onChange={handleChange}
                disabled={!formData.country}
                required
              >
                <option value="" disabled>
                  Select State
                </option>
                {states.map((s) => (
                  <option key={s.isoCode} value={s.isoCode}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group half">
            <div className="select-wrapper">
              <select
                name="city"
                className="form-select"
                value={formData.city}
                onChange={handleChange}
                disabled={!formData.state}
                required
              >
                <option value="" disabled>
                  Select City
                </option>
                {cities.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-group">
          <button
            type="submit"
            className="send-request-button"
            disabled={loading}
          >
            {loading ? "Sending..." : "Send Request"}
          </button>
        </div>
      </form>
    </div>
  );
}
